import {
	ICampaign,
	ICampaignConfig,
	ICampaignForm,
	ICampaignGenAIPayload,
	ICompanyAccount,
} from 'src/lib/schemas';
import { IOption } from 'src/lib/schemas/misc';

export const transformFormToCampaign = (
	form: ICampaignForm,
): Omit<ICampaign, 'id'> => {
	const {
		group,
		title,
		description,
		goal,
		promotedObject,
		destination,
		customerProfiles,
		promotion,
		channels,
		budget,
	} = form;
	// Handle channels as string array (form schema) instead of object
	const selectedChannels = channels || [];

	return {
		title,
		description,
		group: group ?? '',
		goal:
			goal?.map(({ id, target }) => ({
				id: id,
				target: Number(target),
			})) ?? [],
		promotedObject: {
			type: promotedObject.type,
			ids: promotedObject.value.map(({ value }) => value),
		},
		destination,
		customerProfiles,
		promotion,
		channels: selectedChannels,
		budget: {
			...budget,
			currency: budget?.currency ?? 'USD',
			total: Number(budget?.total ?? '0'),
		},
		status: 'draft',
	};
};

export const transformCampaignToForm = (
	campaign: ICampaign,
	promotedObjecOptions: IOption[],
): ICampaignForm => {
	const {
		title,
		description,
		goal,
		promotedObject,
		destination,
		customerProfiles,
		promotion,
		channels,
		budget,
		placements,
	} = campaign;

	console.log('🔄 [transformCampaignToForm] Input:', {
		campaignIds: promotedObject.ids,
		optionsCount: promotedObjecOptions.length,
		sampleOptions: promotedObjecOptions.slice(0, 3).map((o: any) => ({
			value: o.value,
			label: o.label,
			_id: o._id,
			catalogId: o.catalogId,
		})),
	});

	const promotedObjectValue = promotedObject.ids.map((id) => {
		// Try to find matching option by value, _id, or catalogId (handles different ID formats)
		const option = promotedObjecOptions.find(
			(option: any) =>
				option.value === id || option._id === id || option.catalogId === id,
		);
		console.log(`🔄 [transformCampaignToForm] Matching id "${id}":`, {
			foundOption: !!option,
			matchedBy: option
				? option.value === id
					? 'value'
					: (option as any)._id === id
						? '_id'
						: 'catalogId'
				: 'none',
		});
		// Preserve the original ID even if no matching option is found
		// This prevents the campaign from losing its promoted object during auto-save
		return option ?? { value: id, label: id };
	});

	console.log('🔄 [transformCampaignToForm] Result:', {
		promotedObjectValue,
	});

	return {
		title,
		description,
		goal: goal ?? [],
		promotedObject: { type: promotedObject.type, value: promotedObjectValue },
		destination,
		customerProfiles,
		promotion,
		channels,
		budget: budget
			? { ...budget, total: String(budget.total ?? '0') }
			: undefined,
		placements,
	};
};

export const transformCampaignToGenAIPayload = (
	campaign: Omit<ICampaign, 'id'>,
	config: ICampaignConfig | null,
	metaOptions: IOption[],
	tonesOptions: IOption[],
	audienceOptions: IOption[],
): ICampaignGenAIPayload => {
	if (!config) throw new Error('Campaign config is not defined');

	const {
		title,
		description,
		goal,
		promotedObject,
		audience,
		tone,
		promotion,
		channels,
		budget,
	} = campaign;

	const mappedGoals = goal?.map(({ id }) => {
		const goal = config.goals.find((g) => g.value === id);
		if (!goal) return id;
		return goal.label;
	});

	const mappedTones = tone.map((id) => {
		const elem = tonesOptions.find((t) => t.value === id);
		if (!elem) return id;
		return elem.label;
	});

	const mappedAudience = audience.map((id) => {
		const elem = audienceOptions.find((a) => a.value === id);
		if (!elem) return id;
		return elem.label;
	});

	const mappedProducts = promotedObject.ids.map((id) => {
		const prod = metaOptions.find((p) => p.value === id);
		if (!prod) return id;
		return prod.label;
	});

	return {
		name: title,
		description,
		goals: mappedGoals ?? [],
		audiance: mappedAudience,
		tone: mappedTones,
		products: mappedProducts,
		promo_code: promotion?.[0]?.code ?? '',
		promo_description: promotion?.[0]?.description ?? '',
		channels,
		budget: budget ? `USD ${budget.total}` : null,
	};
};

export const transformCampaignToGenAIAppPayload = (
	campaign: Omit<ICampaign, 'id'>,
	config: ICampaignConfig | null,
	metaOptions: IOption[],
	tonesOptions: IOption[],
	audienceOptions: IOption[],
	account: ICompanyAccount | null,
	content: string,
): any => {
	if (!config) throw new Error('Campaign config is not defined');

	const { brief, goal, promotedObject, audience, tone, promotion } = campaign;

	const mappedGoals = goal?.map(({ id }) => {
		const goal = config.goals.find((g) => g.value === id);
		if (!goal) return id;
		return goal.label;
	});

	const mappedAudience = audience.map((id) => {
		const elem = audienceOptions.find((a) => a.value === id);
		if (!elem) return { id, name: id };
		return { id, name: elem.label };
	});

	const mappedProducts = promotedObject.ids.map((id) => {
		const prod = metaOptions.find((p) => p.value === id);
		if (!prod) return id;
		return { id, name: prod.label };
	});

	const mappedTones = tone.map((id) => {
		const elem = tonesOptions.find((t) => t.value === id);
		if (!elem) return { id, name: id };
		return {
			id,
			name: elem.label,
			account: account?.id ?? '',
			type: 'account',
		};
	});

	const promotions = promotion?.length ? promotion : undefined;

	const payload = {
		id: '',
		callback_url: '',

		inputs: {
			content,
			merchantName: account?.name ?? '',
			description: brief?.description ?? '',
			goals: mappedGoals,
			audiences: mappedAudience,
			tones: mappedTones,
			products: mappedProducts,
			promotion: promotions,
		},
		sync: 'true',
	};

	return payload;
};

export const placementIdToChannelIdMap: { [key: string]: string } = {
	facebookFeedSocialMedia: 'facebook',
	facebookStorySocialMedia: 'facebook',
	instagramFeedSocialMedia: 'instagram',
	instagramStorySocialMedia: 'instagram',
	facebookFeedPaidAd: 'facebookPaid',
	facebookStoryPaidAd: 'facebookPaid',
	instagramFeedPaidAd: 'instagramPaid',
	instagramStoryPaidAd: 'instagramPaid',
	googleSearch: 'google',
	googleDISPLAY: 'googleDisplay',
	googleDISPLAY_MAIN: 'googleDisplay',
	googleDISPLAY_HORIZ: 'googleDisplay',
	googleSEARCH: 'google',
	googlePMAX: 'googlePMAX',
	googleDISPLAY_MAIN_PMAX: 'googlePMAX',
	googleDISPLAY_HORIZ_PMAX: 'googlePMAX',
};

export const placementIdToCreativeName: { [key: string]: string } = {
	facebookFeedSocialMedia: 'Feed ',
	facebookStorySocialMedia: 'Story ',
	instagramFeedSocialMedia: 'Feed ',
	instagramStorySocialMedia: 'Story ',
	facebookFeedPaidAd: 'Feed ',
	facebookStoryPaidAd: 'Story ',
	instagramFeedPaidAd: 'Feed ',
	instagramStoryPaidAd: 'Story ',
	googleSearch: 'Search',
	googleDISPLAY: 'Display',
	googleDISPLAY_MAIN: 'Display Main',
	googleDISPLAY_HORIZ: 'Display Horizontal',
	googleSEARCH: 'Search',
	googlePMAX: 'PMax',
	googleDISPLAY_MAIN_PMAX: 'PMax Display Main',
	googleDISPLAY_HORIZ_PMAX: 'PMax Display Horizontal',
};
